/**
 * Starts loading a tab's data a moment before it opens (the pointer over its sidebar entry), so the tab usually opens
 * on loaded content. A prefetched result is used once, and only if it's fresh; otherwise the tab loads it itself.
 */
const FRESH_FOR_MS = 10_000;

interface Prefetched {
  result: Promise<unknown>;
  startedAt: number;
}

const prefetched = new Map<string, Prefetched>();

const isFresh = (entry: Prefetched | undefined, now: number): entry is Prefetched =>
  entry !== undefined && now - entry.startedAt < FRESH_FOR_MS;

/** Starts `load` unless a fresh prefetch of `key` is already under way */
export function prefetch<T>(key: string, load: () => Promise<T>, now = Date.now()): void {
  if (isFresh(prefetched.get(key), now)) return;
  const result = load();
  // A failed prefetch is retried by the tab itself, so it must not surface as an unhandled rejection
  result.catch(() => undefined);
  prefetched.set(key, { result, startedAt: now });
}

/** The prefetched result of `key` if it's fresh (then forgotten), else a new `load()` */
export function loadPrefetched<T>(key: string, load: () => Promise<T>, now = Date.now()): Promise<T> {
  const entry = prefetched.get(key);
  prefetched.delete(key);
  if (!isFresh(entry, now)) return load();
  return (entry.result as Promise<T>).catch(() => load());
}

/** For tests */
export function clearPrefetched(): void {
  prefetched.clear();
}
